import { type Fixture } from '@h2h/shared';
import { Lightbulb } from '../../ui/icons';
import { cn } from '../../../lib/cn';
import { signedPoints } from '../../../lib/format';
import {
  CARD_AT_RISK,
  draftToWin,
  isCardStarted,
  type DraftCard
} from '../../../lib/picks';
import { BASE } from './tokens';
import { FixtureOverviewCard } from './FixtureOverviewCard';

/** Projected-round hero + fixture list (tap to build a card). */
export function RoundOverview({
  fixtures,
  cardByFixture,
  now,
  roundLabel,
  flag = false,
  hideHeader = false,
  onOpen
}: {
  fixtures: Fixture[];
  cardByFixture: Map<string, DraftCard>;
  now: number;
  roundLabel?: string;
  flag?: boolean;
  /** Skip the projected-score hero + "how it works" card — the host (e.g. the
   *  League Hub) renders its own combined header above the fixture list. */
  hideHeader?: boolean;
  onOpen: (fixtureId: string) => void;
}) {
  const built = fixtures
    .map((f) => cardByFixture.get(f.fixtureId))
    .filter((c): c is DraftCard => !!c && isCardStarted(c));
  const toWin = built.reduce((sum, c) => sum + draftToWin(c), 0);
  const atRisk = built.length * CARD_AT_RISK;

  return (
    <div className={cn(!hideHeader && 'pt-3.5', hideHeader && 'pt-1')}>
      {!hideHeader && (
      <>
      {roundLabel && (
        <div className="px-[4px] pb-0 pt-[14px]">
          <div className="text-[12px] font-extrabold uppercase tracking-[0.14em] text-[#8A8A92]">{roundLabel}</div>
          <h1 className="mt-2 text-[30px] font-extrabold leading-none tracking-[-0.035em] text-ink">Build your cards</h1>
          <p className="mt-[5px] text-[13px] font-medium text-[#8A8A92]">Pick as many matches as you want this round.</p>
        </div>
      )}

      <div className="mt-4 overflow-hidden rounded-[20px] border border-black/[0.07] bg-white px-[18px] py-[15px] shadow-[0_18px_40px_-22px_rgba(20,20,50,0.3)]">
        {/* Numbers row */}
        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="text-[9px] font-extrabold uppercase tracking-[0.08em] text-ink3">To win</div>
            <div className="num mt-0.5 text-[32px] font-extrabold leading-none tracking-[-0.04em] text-[#0A9A72]">
              {built.length > 0 ? toWin : '—'}
            </div>
          </div>
          <div className="flex items-end gap-[11px] pb-[2px]">
            <div className="text-right">
              <div className="text-[9px] font-extrabold uppercase tracking-[0.08em] text-ink3">At risk</div>
              <div className="num mt-0.5 text-[17px] font-extrabold tracking-[-0.02em] text-[#EF4135]">
                {built.length > 0 ? signedPoints(atRisk) : '—'}
              </div>
            </div>
          </div>
        </div>

        {/* Progress: n / total cards + bar */}
        <div className="mt-[13px] border-t border-black/[0.06] pt-[13px]">
          <div className="mb-[9px] flex items-center justify-between">
            <span className="text-[12px] font-semibold text-ink2">
              <span className="num font-extrabold text-ink">{built.length}</span>
              {' / '}
              <span className="num font-bold">{fixtures.length}</span>
              {' '}card{fixtures.length === 1 ? '' : 's'} built
            </span>
            {built.length > 0 && built.length === fixtures.length && (
              <span className="inline-flex items-center gap-[5px] rounded-[7px] bg-accent/[0.1] px-[8px] py-[3px] text-[10px] font-extrabold text-accent-ink">
                All done
              </span>
            )}
          </div>
          <div className="h-[4px] overflow-hidden rounded-full bg-[#EBEBF0]">
            <div
              className="h-full rounded-full bg-[linear-gradient(90deg,#2EDB7B,#0BA078)] transition-[width] duration-300"
              style={{ width: built.length === 0 ? '0%' : `${Math.max(4, (built.length / Math.max(1, fixtures.length)) * 100)}%` }}
            />
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-[16px] border border-black/[0.08] bg-white px-[15px] py-[13px]">
        <div className="flex flex-wrap items-center gap-[9px]">
          <span className="inline-flex items-center gap-[5px] rounded-[7px] bg-[rgba(227,168,43,0.14)] px-2 py-1 text-[10px] font-extrabold uppercase tracking-[0.04em] text-[#B5740A]">
            <Lightbulb width={12} height={12} strokeWidth={2.2} className="text-[#B5740A]" />
            How it works
          </span>
          <span className="flex items-center gap-[7px] text-[15px] font-extrabold tracking-[-0.025em] text-ink">
            <span className="text-[#0A9A72]">{BASE}</span>
            <span className="text-[#C4C4CC]">×</span>
            <span>boosts</span>
            <span className="text-[#C4C4CC]">=</span>
            <span className="text-[#0A9A72]">to&nbsp;win</span>
          </span>
        </div>
        <p className="mt-[9px] text-[11.5px] font-medium leading-[1.4] text-[#8A8A92]">
          Get the winner wrong and you lose <strong className="text-[#EF4135]">{signedPoints(CARD_AT_RISK)}</strong> on that card — even if your boosts were right. Play one match or all of them.
        </p>
      </div>
      </>
      )}

      <div className={cn('mb-3 flex items-baseline justify-between', hideHeader ? 'mt-1' : 'mt-4 px-1')}>
        <span className="text-[14px] font-extrabold tracking-[-0.01em] text-ink">Fixtures this round</span>
        <span className="text-[12px] font-semibold text-[#8A8A92]">{fixtures.length} matches</span>
      </div>

      <div className="flex flex-col gap-[10px] md:grid md:grid-cols-2">
        {fixtures.map((f) => (
          <FixtureOverviewCard
            key={f.fixtureId}
            fixture={f}
            card={cardByFixture.get(f.fixtureId)}
            now={now}
            flag={flag}
            onOpen={onOpen}
          />
        ))}
      </div>
    </div>
  );
}
